import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Alert,
  Button,
  Chip,
  Flex,
  FlexColumn,
  Loading,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import SensitiveInfoDisplay from 'settings/components/shared/SensitiveInfoDisplay';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import {
  GenerateJiraWebhookSecretResponse,
  JiraWebhookConfig,
} from 'shared/types';
import {
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

type Props = {
  envId: string;
  integrationId: string;
  onStatusChange?: (isConfigured: boolean, isError: boolean) => void;
};

const WarningText = styled(Typography)`
  font-size: ${fontSize('sm')};
`;

const StepWrapper = styled(FlexColumn)`
  gap: ${space('sm')};
`;

const StepTitle = styled(Typography)`
  font-weight: ${fontWeight('semibold')};
`;

const StepBody = styled.ul`
  margin: 0;
  padding-left: ${space('xl')};
`;

const ListItem = styled.li`
  font-size: ${fontSize('base')};
  line-height: ${lineHeight('loose')};
  margin-bottom: ${space('xs')};
`;

const Kbd = styled.code`
  font-size: ${fontSize('xs')};
  font-family: ${fontFamily('monoSystem')};
  background: ${({ theme }) => theme.palette.surface.translucent.selected};
  border: 1px solid ${({ theme }) => theme.palette.border.translucent};
  border-radius: ${radius('xs')};
  padding: ${space('hairline', 'xsPlus')};
`;

const Bold = styled.span`
  font-weight: ${fontWeight('semibold')};
`;

const JiraWebhookSection = ({
  envId,
  integrationId,
  onStatusChange,
}: Props) => {
  const [generatedSecret, setGeneratedSecret] =
    useState<GenerateJiraWebhookSecretResponse | null>(null);
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
  const { openSnackbar } = useSnackbar();

  const configQuery = useQuery<JiraWebhookConfig>(
    ['jira-webhook-config', integrationId],
    () => api.fetchJiraWebhookConfig(envId, integrationId),
  );

  const generateMutation = useMutation(
    () => api.generateJiraWebhookSecret(envId, integrationId),
    {
      onSuccess: (data) => {
        setGeneratedSecret(data);
        setShowRegenerateWarning(false);
        configQuery.refetch();
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to generate webhook secret',
          variant: 'error',
        });
      },
    },
  );

  const config = configQuery.data;
  const webhookUrlPath =
    generatedSecret?.webhook_url_path ?? config?.webhook_url_path ?? '';
  const webhookUrl = webhookUrlPath
    ? `${import.meta.env.VITE_API_BASE_URL}${webhookUrlPath}`
    : '';
  const hasSecret = config?.has_secret || generatedSecret !== null;
  const isConfigured = config?.webhook_configured ?? false;
  const urlLoadFailed =
    configQuery.isError || (!configQuery.isLoading && !webhookUrl);

  useEffect(() => {
    if (!configQuery.isLoading) {
      onStatusChange?.(isConfigured, configQuery.isError);
    }
  }, [
    isConfigured,
    configQuery.isError,
    configQuery.isLoading,
    onStatusChange,
  ]);

  if (configQuery.isLoading) {
    return <Loading />;
  }

  return (
    <FlexColumn gap={2}>
      <StepWrapper>
        <StepTitle>Step 1 — Open Jira Webhook Settings</StepTitle>
        <StepBody>
          <ListItem>
            Log in to your Jira account as an <Bold>Admin</Bold>
          </ListItem>
          <ListItem>
            Click the <Bold>Settings</Bold> icon (top right)
          </ListItem>
          <ListItem>
            Click <Kbd>System</Kbd> under <Kbd>Jira admin settings</Kbd>
          </ListItem>
          <ListItem>
            In the left sidebar, scroll down and click <Kbd>WebHooks</Kbd>
          </ListItem>
          <ListItem>
            Click <Kbd>Create a WebHook</Kbd> (top right corner)
          </ListItem>
        </StepBody>
      </StepWrapper>

      <StepWrapper>
        <StepTitle>Step 2 — Fill in the Webhook Details</StepTitle>
        <StepBody>
          <ListItem>
            <Bold>Name:</Bold> anything descriptive e.g.{' '}
            <Kbd>Elementary Sync</Kbd>
          </ListItem>
          <ListItem>
            <Bold>Status:</Bold> <Kbd>Enabled</Kbd>
          </ListItem>
          <ListItem>
            <Bold>URL:</Bold> copy and paste the webhook URL below
            {urlLoadFailed ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                <WarningText>
                  Failed to load webhook URL. Try reopening this dialog.
                </WarningText>
              </Alert>
            ) : webhookUrl ? (
              <SensitiveInfoDisplay value={webhookUrl} label="" />
            ) : (
              <Loading />
            )}
          </ListItem>
          <ListItem>
            <Bold>Secret:</Bold> generate and copy the secret below, then paste
            it into Jira
            <FlexColumn gap={1} sx={{ mt: 0.5 }}>
              {generatedSecret ? (
                <>
                  <SensitiveInfoDisplay
                    value={generatedSecret.secret}
                    label="Secret"
                  />
                  <Alert severity="warning">
                    <WarningText>
                      Copy this secret now — it will not be shown again.
                    </WarningText>
                  </Alert>
                </>
              ) : !hasSecret ? (
                <Button
                  variant="outlined"
                  size="extraSmall"
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isLoading}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Generate Secret
                </Button>
              ) : (
                <Flex gap={1} alignItems="center">
                  <Chip label="Secret configured" color="idle" size="medium" />
                  {showRegenerateWarning ? (
                    <>
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        <WarningText>
                          Regenerating will immediately invalidate the current
                          secret. You must update your Jira webhook with the new
                          secret.
                        </WarningText>
                      </Alert>
                      <Flex gap={1}>
                        <Button
                          variant="outlined"
                          size="extraSmall"
                          onClick={() => generateMutation.mutate()}
                          disabled={generateMutation.isLoading}
                        >
                          Confirm Regenerate
                        </Button>
                        <Button
                          variant="text"
                          size="extraSmall"
                          onClick={() => setShowRegenerateWarning(false)}
                        >
                          Cancel
                        </Button>
                      </Flex>
                    </>
                  ) : (
                    <Button
                      variant="text"
                      size="extraSmall"
                      onClick={() => setShowRegenerateWarning(true)}
                    >
                      Regenerate
                    </Button>
                  )}
                </Flex>
              )}
            </FlexColumn>
          </ListItem>
        </StepBody>
      </StepWrapper>

      <StepWrapper>
        <StepTitle>Step 3 — Select Events</StepTitle>
        <StepBody>
          <ListItem>
            Scroll down to the <Bold>Events</Bold> section
          </ListItem>
          <ListItem>
            Under <Kbd>Issue</Kbd> → check <Kbd>updated</Kbd> only
          </ListItem>
          <ListItem>Leave all other events unchecked</ListItem>
        </StepBody>
      </StepWrapper>

      <StepWrapper>
        <StepTitle>Step 4 — Scope to Your Project (Recommended)</StepTitle>
        <StepBody>
          <ListItem>
            Under <Bold>JQL Filter</Bold> enter:{' '}
            <Kbd>project = {`"YOUR_PROJECT_KEY"`}</Kbd>
          </ListItem>
          <ListItem>
            Replace <Kbd>YOUR_PROJECT_KEY</Kbd> with your actual Jira project
            key (e.g. <Kbd>PROJ</Kbd>)
          </ListItem>
          <ListItem>
            This ensures only events from this project are sent
          </ListItem>
        </StepBody>
      </StepWrapper>

      <StepWrapper>
        <StepTitle>Step 5 — Save & Verify</StepTitle>
        <StepBody>
          <ListItem>
            Click <Kbd>Create</Kbd> at the bottom in Jira
          </ListItem>
          <ListItem>
            The status at the bottom will turn <Bold>green</Bold> automatically
            once Jira sends the first event to Elementary
          </ListItem>
        </StepBody>
      </StepWrapper>
    </FlexColumn>
  );
};

export default JiraWebhookSection;
